import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import type { VideoMirrorProps } from '../VideoMirror';

export const StreamMirror: FunctionComponent<VideoMirrorProps> = ({ videoElement }) => {
  const mirrorRef = useRef<HTMLVideoElement>(null);

  const captureStreamUrl = useCallback(() => {
    const mirrorElement = mirrorRef.current;
    if (!videoElement || !mirrorElement) return;
    const stream = videoElement.captureStream?.() ?? videoElement.mozCaptureStream?.();
    if (!stream) {
      throw new Error('StreamMirror is rendering when not supported');
    }

    // Older browsers may not have srcObject
    // Source of this fallback: https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/srcObject#supporting_fallback_to_the_src_property
    let streamObjectUrl: string | null = null;
    if ('srcObject' in mirrorElement) {
      try {
        mirrorElement.srcObject = stream;
      } catch (error) {
        if (error instanceof TypeError) {
          throw error;
        }
        // Avoid using this in new browsers, as it is going away.
        streamObjectUrl = URL.createObjectURL(stream as unknown as MediaSource);
        (mirrorElement as HTMLVideoElement).src = streamObjectUrl;
      }
    } else {
      // Avoid using this in new browsers, as it is going away.
      streamObjectUrl = URL.createObjectURL(stream as unknown as MediaSource);
      (mirrorElement as HTMLVideoElement).src = streamObjectUrl;
    }
    return streamObjectUrl;
  }, [videoElement]);

  useEffect(() => {
    if (!videoElement || !mirrorRef.current) return;
    // initialize the streamObjectUrl
    let streamObjectUrl = captureStreamUrl();

    const observer = new MutationObserver(() => {
      streamObjectUrl = captureStreamUrl(); // trigger re-run of stream setup whenever there is a change in stream src
    });

    // Observe src changes
    observer.observe(videoElement, {
      attributes: true,
      attributeFilter: ['src'],
    });

    return (): void => {
      if (streamObjectUrl) {
        URL.revokeObjectURL(streamObjectUrl);
      }
      observer.disconnect();
    };
  }, [videoElement, captureStreamUrl]);

  return <video ref={mirrorRef} autoPlay playsInline muted />;
};
